import React from 'react';

import { AuthLevel, AuthStatus } from 'scbk-services/auth/constants';
import useAuthStatus from 'scbk-services/auth/hooks/useAuthStatus';
import { Envs } from 'scbk-services/config/enums/envs';
import { useEnvContext } from 'scbk-services/config/hooks/useEnvContext';
import useAuthRouterStore from 'scbk-services/router/stores/useAuthRouterStore';

interface AuthRouteProps {
    fallbackPath: string;
    navigate: React.ComponentType<{ replace: boolean; to: string }>;
    outlet: React.ComponentType;
    requiredAuthLevel: AuthStatus;
}

const meetsAuthRequirement = (
    userAuthStatus: AuthStatus,
    requiredAuthStatus: AuthStatus,
): boolean => {
    const authMapper = {
        [AuthStatus.UNAUTHENTICATED]: AuthLevel.NONE,
        [AuthStatus.PASSWORD_VERIFIED]: AuthLevel.PASSWORD,
        [AuthStatus.LOGIN]: AuthLevel.LOGIN,
    };

    return authMapper[userAuthStatus] >= authMapper[requiredAuthStatus];
};

const AuthRoute: React.FC<AuthRouteProps> = ({
    fallbackPath,
    navigate: Navigate,
    outlet: Outlet,
    requiredAuthLevel = AuthStatus.LOGIN,
}) => {
    const { getStatus: getUserAuthStatus } = useAuthStatus();
    const currentRoutingPath = useAuthRouterStore((state) => state.currentRoutingPath);
    const setPendingAuthenticatedRoutePath = useAuthRouterStore(
        (state) => state.setPendingAuthenticatedRoutePath,
    );

    const { getEnv } = useEnvContext();
    if (getEnv() === Envs.MOCK) {
        return <Outlet />;
    }
    const hasPermission = meetsAuthRequirement(getUserAuthStatus(), requiredAuthLevel);

    if (!hasPermission) {
        setPendingAuthenticatedRoutePath(currentRoutingPath);
    }

    return hasPermission ? <Outlet /> : <Navigate replace to={fallbackPath} />;
};

export { AuthRoute, meetsAuthRequirement };
